import { useState } from "react";
import { useMutation } from "@apollo/client/react";
import { FormattedMessage, useIntl, type IntlShape } from "react-intl";
import { Button } from "@/components/design/Button";
import { Modal } from "@/components/design/Modal";
import { SetListingAccessDocument } from "@/graphql/generated/operations";
import {
  LISTING_ACCESS_CHANGED,
  LISTING_ACCESS_DELEGATION_REFUSED,
  LISTING_ACCESS_NOT_MANAGED,
  LISTING_APPLICATIONS_GRANT_REFUSED,
} from "@/lib/genesis-error-codes";
import { graphQlErrorExtensions } from "@/lib/graphql-error";
import { userErrorMessage } from "@/lib/user-error";
import type { SettingsProps } from "@/pages/project/dashboard-kinds";
import { listingAccessInput, membersFromAccess, sameMembers, type ListingMemberDraft } from "./listing-access";
import { ListingMembersEditor } from "./ListingMembersEditor";
import { forgetListingAccess, useListing, type ListingDetail } from "./use-listing";

// Mitglieder und ihre Rechte. Geschrieben über setListingAccess, nicht updateListing: Zugriffe verwalten ist
// vom Bearbeiten des Inhalts getrennt (@external-adr GENESIS-0095-managing-listing-access-is-separate-from-editing-it).
// Gespeichert wird gegen den beim Öffnen gelesenen Stand (`accessRevision`); hat ihn inzwischen jemand
// geändert, lehnt genesis ab und nichts wird überschrieben.

export function ListingSettingsModal({ ctx, onClose }: SettingsProps) {
  // Dieselbe Abfrage wie die Quelle des Dashboards, also aus dem Cache.
  const { listing } = useListing(ctx.entity.id);
  if (!listing) return null;
  return <SettingsForm listing={listing} onClose={onClose} />;
}

function SettingsForm({
  listing,
  onClose,
}: {
  listing: ListingDetail;
  onClose: () => void;
}) {
  const intl = useIntl();
  const [setListingAccess] = useMutation(SetListingAccessDocument);
  // Der Stand beim Öffnen, eingefroren: gegen ihn wird beim Speichern verglichen, auch wenn der Cache
  // währenddessen eine neuere Fassung bekommt.
  const [initial] = useState<ListingMemberDraft[]>(() => membersFromAccess(listing.access, listing.createdBy?.id).map(member => {
    const avatar = listing.access?.find(entry => entry.person.id === member.personId)?.person.avatar;
    return { ...member, avatarContent: avatar?.content, avatarMime: avatar?.mimeType };
  }));
  const [revision] = useState(() => listing.accessRevision ?? "");
  const [members, setMembers] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (saving || !listing.actions.canManageAccess) return;
    setSaving(true);
    setError(null);
    try {
      if (!sameMembers(members, initial)) {
        await setListingAccess({
          variables: { input: { listingId: listing.id, access: listingAccessInput(members), expectedRevision: revision } },
          update: forgetListingAccess,
        });
      }
      onClose();
    } catch (e) {
      setError(accessErrorMessage(intl, e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      id="listing-settings-modal"
      onClose={onClose}
      title={<FormattedMessage id="share.listings.settings.title" defaultMessage="Mitglieder und Berechtigungen" />}
      maxWidth="48rem"
      footer={
        <>
          <Button id="listing-settings-cancel" variant="secondary" size="compact" onClick={onClose} disabled={saving}>
            <FormattedMessage id="common.cancel" defaultMessage="Abbrechen" />
          </Button>
          <Button
            id="listing-settings-save"
            variant="primary"
            size="compact"
            onClick={() => void save()}
            disabled={saving || !listing.actions.canManageAccess}
          >
            {saving ? (
              <FormattedMessage id="common.saving" defaultMessage="Speichern…" />
            ) : (
              <FormattedMessage id="common.save" defaultMessage="Speichern" />
            )}
          </Button>
        </>
      }
    >
      <ListingMembersEditor
        owner={listing.createdBy ? { id: listing.createdBy.id, name: listing.createdBy.formattedName ?? "",
          avatarContent: listing.createdBy.avatar?.content, avatarMime: listing.createdBy.avatar?.mimeType } : null}
        members={members}
        onChange={setMembers}
        canEdit={listing.actions.canManageAccess && !saving}
        canAppointManagers={listing.actions.canAppointManagers && !saving}
        grantableApplications={listing.actions.grantableApplications}
      />
      {error ? (
        <p role="alert" className="mt-5 text-smycles-meta text-ink-accent">
          {error}
        </p>
      ) : null}
    </Modal>
  );
}

/** Die vier Ablehnungen, die der Leser selbst auflösen kann, in eigenen Worten; alles andere generisch. */
function accessErrorMessage(intl: IntlShape, error: unknown): string {
  if (graphQlErrorExtensions(error, LISTING_ACCESS_CHANGED)) {
    return intl.formatMessage({
      id: "share.listings.access.changed",
      defaultMessage: "Die Zugriffe wurden inzwischen geändert. Bitte schließen und neu öffnen, dann erneut ändern.",
    });
  }
  if (graphQlErrorExtensions(error, LISTING_ACCESS_DELEGATION_REFUSED)) {
    return intl.formatMessage({
      id: "share.listings.access.delegationRefused",
      defaultMessage: "Nur die Person, die die Ausschreibung angelegt hat, ernennt Verwaltende und ändert deren Rechte.",
    });
  }
  if (graphQlErrorExtensions(error, LISTING_APPLICATIONS_GRANT_REFUSED)) {
    return intl.formatMessage({
      id: "share.listings.access.applicationsGrantRefused",
      defaultMessage: "Bei Bewerbungen kannst du höchstens deine eigene Stufe vergeben. Höhere Rechte vergibt die Person, die die Ausschreibung angelegt hat.",
    });
  }
  if (graphQlErrorExtensions(error, LISTING_ACCESS_NOT_MANAGED)) {
    return intl.formatMessage({
      id: "share.listings.access.notManaged",
      defaultMessage: "Du darfst die Zugriffe dieser Ausschreibung nicht (mehr) verwalten.",
    });
  }
  return userErrorMessage(intl, error);
}
